import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import BasicLayout from "layouts/authentication/components/BasicLayout";
import {
  getAuth,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from "firebase/auth";
import bgImage from "assets/images/bg-reset-cover.jpeg";
import { useTranslation } from "react-i18next";

function ResetPassword() {
  const [step, setStep] = useState(1);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation("auth");

  const handleVerifyOldPassword = async (e) => {
    e.preventDefault();
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      setError(t("resetPassword.errorNotLoggedIn"));
      return;
    }

    if (!oldPassword) {
      setError(t("resetPassword.errorEnterCurrentPassword"));
      return;
    }

    try {
      const credential = EmailAuthProvider.credential(user.email, oldPassword);
      await reauthenticateWithCredential(user, credential);
      setStep(2);
      setError(null);
    } catch (error) {
      console.error("Error verifying password:", error);
      setError(t("resetPassword.errorIncorrectPassword"));
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    const auth = getAuth();
    const user = auth.currentUser;

    if (newPassword !== confirmPassword) {
      setError(t("resetPassword.errorPasswordsDoNotMatch"));
      return;
    }

    try {
      await updatePassword(user, newPassword);
      setSuccess(true);
      setError(null);
      setTimeout(() => navigate("/authentication/sign-in"), 3000);
    } catch (error) {
      console.error("Error resetting password:", error);
      setError(t("resetPassword.errorResetFailed", { error: error.message }));
    }
  };

  return (
    <BasicLayout image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            {step === 1 ? t("resetPassword.verifyTitle") : t("resetPassword.resetTitle")}
          </MDTypography>
        </MDBox>

        <MDBox pt={4} pb={3} px={3}>
          {step === 1 ? (
            <MDBox component="form" role="form" onSubmit={handleVerifyOldPassword}>
              <MDBox mb={2}>
                <MDInput
                  type="password"
                  label={t("resetPassword.currentPasswordLabel")}
                  fullWidth
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                />
              </MDBox>
              {error && (
                <MDTypography variant="body2" color="error" textAlign="center" mb={2}>
                  {error}
                </MDTypography>
              )}
              <MDBox mt={4} mb={1}>
                <MDButton variant="gradient" color="info" fullWidth type="submit">
                  {t("resetPassword.verifyButton")}
                </MDButton>
              </MDBox>
              <MDBox mt={3} mb={1} textAlign="center">
                <MDTypography variant="button" color="text">
                  <MDTypography
                    component={Link}
                    to="/authentication/forget-password"
                    variant="button"
                    color="info"
                    fontWeight="medium"
                    textGradient
                  >
                    {t("forgotPasswordLink")}
                  </MDTypography>{" "}
                  {/* ✅ Corrected Closing Tag */}
                </MDTypography>
              </MDBox>
            </MDBox>
          ) : (
            <MDBox component="form" role="form" onSubmit={handleResetPassword}>
              <MDBox mb={2}>
                <MDInput
                  type="password"
                  label={t("resetPassword.newPasswordLabel")}
                  fullWidth
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </MDBox>
              <MDBox mb={2}>
                <MDInput
                  type="password"
                  label={t("resetPassword.confirmPasswordLabel")}
                  fullWidth
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </MDBox>
              {error && (
                <MDTypography variant="body2" color="error" textAlign="center" mb={2}>
                  {error}
                </MDTypography>
              )}
              {success && (
                <MDTypography variant="body2" color="success" textAlign="center" mb={2}>
                  {t("resetPassword.successMessage")}
                </MDTypography>
              )}
              <MDBox mt={4} mb={1}>
                <MDButton variant="gradient" color="info" fullWidth type="submit">
                  {t("resetPassword.resetButton")}
                </MDButton>
              </MDBox>
            </MDBox>
          )}
        </MDBox>
      </Card>
    </BasicLayout>
  );
}

export default ResetPassword;
